<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "dashboard/composables/store";
import { useAccount } from "dashboard/composables/useAccount";
import { getSourceConfig } from "./contactTrackingConfig";

const props = defineProps({
  contactId: { type: Number, required: true },
});

const store = useStore();
const { accountId } = useAccount();

const expandedIds = ref(new Set());

const isLoading = computed(() => store.getters["orders/getUIFlags"].isFetching);
const orders = computed(() => store.getters["orders/getOrders"]);

const loadData = () => {
  store.dispatch("orders/fetchOrders", {
    accountId: accountId.value,
    params: { contact_id: props.contactId, per_page: 50 },
  });
};

onMounted(loadData);
watch(() => props.contactId, loadData);

const toggleOrder = (id) => {
  if (expandedIds.value.has(id)) {
    expandedIds.value.delete(id);
  } else {
    expandedIds.value.add(id);
  }
  expandedIds.value = new Set(expandedIds.value);
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const months = [
    "jan",
    "fev",
    "mar",
    "abr",
    "mai",
    "jun",
    "jul",
    "ago",
    "set",
    "out",
    "nov",
    "dez",
  ];
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return `${d.getDate()} ${months[d.getMonth()]}, ${time}`;
};

const formatCurrency = (val) => {
  if (!val && val !== 0) return null;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(val);
};

const statusLabel = (order) => {
  if (order.financial_status === "paid") return "Pago";
  if (order.financial_status === "refunded") return "Reembolsado";
  if (order.financial_status === "voided") return "Cancelado";
  return "Pendente";
};

const statusClass = (order) => {
  if (order.financial_status === "paid") return "text-n-teal-11 bg-n-teal-3";
  if (order.financial_status === "refunded")
    return "text-n-amber-11 bg-n-amber-3";
  if (order.financial_status === "voided") return "text-n-ruby-11 bg-n-ruby-3";
  return "text-n-slate-10 bg-n-solid-3";
};

const fulfillmentLabel = (order) => {
  if (order.fulfillment_status === "delivered") return "Entregue";
  if (order.fulfillment_status === "shipped") return "Enviado";
  if (order.fulfillment_status === "cancelled") return "Cancelado";
  return "Não enviado";
};

const sourceLabel = (order) => {
  const parts = [order.utm_source, order.utm_medium].filter(Boolean);
  if (parts.length) return parts.join(" / ");
  if (order.last_touch_source) return order.last_touch_source;
  return "(direct)";
};

const touchIcon = (source, isLast) => {
  if (isLast) return "i-lucide-shopping-bag";
  return getSourceConfig(source).icon;
};

const touchDot = (source, isLast) => {
  if (isLast) return "bg-n-teal-3 border-n-teal-9";
  return getSourceConfig(source).dot;
};

const touchIconColor = (source, isLast) => {
  if (isLast) return "text-n-teal-11";
  return getSourceConfig(source).iconColor;
};

const touchLabel = (tp) => {
  const parts = [tp.source, tp.medium].filter(Boolean);
  return parts.join(" / ") || getSourceConfig(null).label;
};
</script>

<template>
  <div class="flex flex-col gap-0">
    <!-- Header -->
    <div class="px-6 py-4 border-b border-n-weak">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ $t("CONTACT_METRICS.JOURNEY.TITLE") }}
      </h3>
      <p class="text-xs text-n-slate-10 mt-0.5">
        {{ $t("CONTACT_METRICS.JOURNEY.SUBTITLE") }}
      </p>
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="flex flex-col items-center justify-center py-16 gap-2"
    >
      <span class="i-lucide-loader-2 size-6 text-n-slate-10 animate-spin" />
    </div>

    <!-- Empty -->
    <div
      v-else-if="!orders.length"
      class="flex flex-col items-center justify-center py-16 gap-2"
    >
      <span class="i-lucide-receipt size-8 text-n-slate-10" />
      <p class="text-sm text-n-slate-10">
        {{ $t("CONTACT_METRICS.JOURNEY.NO_ORDERS") }}
      </p>
    </div>

    <!-- Orders list -->
    <div v-else class="divide-y divide-n-weak">
      <div v-for="order in orders" :key="order.id">
        <!-- Order summary row -->
        <button
          class="w-full flex items-center gap-4 px-6 py-4 hover:bg-n-alpha-1 transition-colors text-left"
          @click="toggleOrder(order.id)"
        >
          <span
            class="flex items-center justify-center w-8 h-8 rounded-lg bg-n-teal-3 flex-shrink-0"
          >
            <span class="i-lucide-receipt size-4 text-n-teal-11" />
          </span>

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t("CONTACT_METRICS.JOURNEY.ORDER") }}
                {{ order.order_number || `#${order.id}` }}
              </span>
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
                :class="statusClass(order)"
              >
                {{ statusLabel(order) }}
              </span>
            </div>
            <div class="flex items-center gap-2 mt-0.5 flex-wrap">
              <span class="text-xs text-n-slate-10">
                {{ formatDate(order.ordered_at) }}
              </span>
              <span
                v-if="order.utm_source || order.last_touch_source"
                class="inline-flex items-center gap-1 text-xs text-n-slate-10"
              >
                <span class="size-1.5 rounded-full bg-n-slate-8" />
                {{ sourceLabel(order) }}
              </span>
            </div>
          </div>

          <div class="text-right flex-shrink-0">
            <span class="text-sm font-semibold text-n-teal-11">
              {{ formatCurrency(order.total) }}
            </span>
          </div>

          <span
            class="i-lucide-chevron-down size-4 text-n-slate-10 transition-transform flex-shrink-0"
            :class="expandedIds.has(order.id) ? 'rotate-180' : ''"
          />
        </button>

        <!-- Expanded detail -->
        <div
          v-if="expandedIds.has(order.id)"
          class="px-6 pt-3 pb-5 bg-n-alpha-1"
        >
          <!-- Stats row -->
          <div class="grid grid-cols-3 gap-3 mb-4">
            <div class="bg-n-solid-1 rounded-lg p-3 border border-n-weak">
              <p class="text-xs text-n-slate-10 mb-1">
                {{ $t("CONTACT_METRICS.JOURNEY.FULFILLMENT") }}
              </p>
              <p class="text-sm font-medium text-n-slate-12">
                {{ fulfillmentLabel(order) }}
              </p>
            </div>
            <div class="bg-n-solid-1 rounded-lg p-3 border border-n-weak">
              <p class="text-xs text-n-slate-10 mb-1">
                {{ $t("CONTACT_METRICS.JOURNEY.SUBTOTAL") }}
              </p>
              <p class="text-sm font-medium text-n-slate-12">
                {{ formatCurrency(order.subtotal) }}
              </p>
            </div>
            <div class="bg-n-solid-1 rounded-lg p-3 border border-n-weak">
              <p class="text-xs text-n-slate-10 mb-1">
                {{ $t("CONTACT_METRICS.JOURNEY.DISCOUNT") }}
              </p>
              <p class="text-sm font-medium text-n-slate-12">
                {{
                  order.discount_amount
                    ? formatCurrency(order.discount_amount)
                    : "—"
                }}
              </p>
            </div>
          </div>

          <!-- Order items -->
          <div
            v-if="order.order_items && order.order_items.length"
            class="rounded-xl border border-n-weak overflow-hidden bg-n-solid-1"
          >
            <div class="px-4 py-2 bg-n-solid-3 border-b border-n-weak">
              <span
                class="text-xs font-medium text-n-slate-10 uppercase tracking-wider"
              >
                {{ $t("CONTACT_METRICS.JOURNEY.ITEMS") }}
              </span>
            </div>
            <div
              v-for="(item, i) in order.order_items"
              :key="item.id || i"
              class="flex items-center gap-3 px-4 py-2.5"
              :class="
                i < order.order_items.length - 1 ? 'border-b border-n-weak' : ''
              "
            >
              <span
                class="i-lucide-package size-3.5 text-n-slate-10 flex-shrink-0"
              />
              <span class="text-sm text-n-slate-12 flex-1 min-w-0 truncate">
                {{ item.product_name || item.title || item.sku || `Item #${item.id}` }}
              </span>
              <span class="text-xs text-n-slate-10 flex-shrink-0">{{
                `${item.quantity}x`
              }}</span>
              <span class="text-sm font-medium text-n-slate-12 flex-shrink-0">
                {{ formatCurrency(item.unit_price ?? item.price) }}
              </span>
            </div>
          </div>

          <!-- Touchpoints journey timeline -->
          <div
            v-if="order.touch_points && order.touch_points.length"
            class="mt-3 rounded-lg border border-n-weak bg-n-solid-1 overflow-hidden"
          >
            <div class="px-4 py-2 bg-n-solid-3 border-b border-n-weak flex items-center justify-between">
              <span class="text-xs font-medium text-n-slate-10 uppercase tracking-wider">
                {{ $t("CONTACT_METRICS.JOURNEY.TOUCHPOINTS_LABEL") }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ order.touch_points.length }}
                {{ order.touch_points.length === 1 ? 'toque' : 'toques' }}
              </span>
            </div>
            <div class="px-4 py-4 overflow-x-auto">
              <div class="flex items-start gap-0 min-w-max">
                <div
                  v-for="(tp, idx) in order.touch_points"
                  :key="idx"
                  class="flex items-start gap-0"
                >
                  <!-- Nó do touchpoint -->
                  <div class="flex flex-col items-center gap-1.5">
                    <!-- Dot com ícone -->
                    <div
                      class="w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                      :class="touchDot(tp.source, idx === order.touch_points.length - 1)"
                    >
                      <span
                        class="size-3.5"
                        :class="[
                          touchIcon(tp.source, idx === order.touch_points.length - 1),
                          touchIconColor(tp.source, idx === order.touch_points.length - 1)
                        ]"
                      />
                    </div>
                    <!-- Label source/medium -->
                    <span class="text-[10px] text-n-slate-11 font-medium text-center leading-tight max-w-[72px]">
                      {{ tp.source }}
                    </span>
                    <span
                      v-if="tp.medium"
                      class="text-[10px] text-n-slate-9 text-center leading-tight max-w-[72px] -mt-1"
                    >
                      {{ tp.medium }}
                    </span>
                  </div>

                  <!-- Conector entre toques -->
                  <div
                    v-if="idx < order.touch_points.length - 1"
                    class="flex items-center self-start mt-4 mx-1"
                  >
                    <div class="w-8 h-px bg-n-weak" />
                    <span class="i-lucide-chevron-right size-3 text-n-slate-8 -ml-1.5" />
                  </div>

                  <!-- Ícone de conversão após o último toque -->
                  <div
                    v-if="idx === order.touch_points.length - 1"
                    class="flex items-center self-start mt-4 mx-1"
                  >
                    <div class="w-6 h-px bg-n-teal-6" />
                    <span class="i-lucide-chevron-right size-3 text-n-teal-9 -ml-1.5" />
                    <div class="w-8 h-8 rounded-full bg-n-teal-3 border-2 border-n-teal-9 flex items-center justify-center ml-1">
                      <span class="i-lucide-shopping-bag size-3.5 text-n-teal-11" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Fallback: attribution simples se não há touchpoints (pedidos sem eventos) -->
          <div
            v-else-if="order.first_touch_source || order.last_touch_source"
            class="mt-3 p-3 rounded-lg border border-n-weak bg-n-solid-1"
          >
            <p class="text-xs font-medium text-n-slate-10 uppercase tracking-wider mb-2">
              {{ $t("CONTACT_METRICS.JOURNEY.ATTRIBUTION") }}
            </p>
            <div class="flex items-center gap-3 flex-wrap">
              <div v-if="order.first_touch_source" class="flex items-center gap-1.5">
                <span class="size-1.5 rounded-full bg-n-blue-9" />
                <span class="text-xs text-n-slate-10">{{ $t("CONTACT_METRICS.JOURNEY.FIRST_TOUCH") }}:</span>
                <span class="text-xs font-medium text-n-slate-12 font-mono">{{ order.first_touch_source }}</span>
              </div>
              <div v-if="order.last_touch_source" class="flex items-center gap-1.5">
                <span class="size-1.5 rounded-full bg-n-teal-9" />
                <span class="text-xs text-n-slate-10">{{ $t("CONTACT_METRICS.JOURNEY.LAST_TOUCH") }}:</span>
                <span class="text-xs font-medium text-n-slate-12 font-mono">{{ order.last_touch_source }}</span>
              </div>
            </div>
          </div>

          <!-- Notes -->
          <div v-if="order.notes" class="mt-3 text-xs text-n-slate-10 italic">
            {{ order.notes }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
